import React, {useState, useCallback, useRef, useEffect} from 'react';
import paper from 'paper';
import {useTranslation} from "@/lib/i18n";
import Header from '@/components/svg-editor/header-panel/header';
import Sidebar from '@/components/svg-editor/tools-panel/sidebar';
import LayersPanel from '@/components/svg-editor/layers-panel/layers-panel';
import Canvas, {clearSelection, drawGrid} from '@/components/svg-editor/canvas/index';
import {useEditorHotkeys} from '@/components/svg-editor/use-editor-hotkeys';
import {useAutoSave} from '@/components/svg-editor/use-auto-save';
import {exportSvgByColor} from '@/components/svg-editor/header-panel/export-symbol';
import { FULL_LARGE_GLASS_SIZE, FLATTEN_TOLERANCE_MM } from '@/constants/editor';

export default function SymbolEditor() {
  const {t} = useTranslation('editor');
  const [activeTool, setActiveTool] = useState('select');
  const [selectedCount, setSelectedCount] = useState(0);
  const [showLayers, setShowLayers] = useState(true);
  const [itemsList, setItemsList] = useState([]);
  const [projectName, setProjectName] = useState(t('new_project'));
  const [customGuides, setCustomGuides] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem('editor_theme') || 'dark');
  const [showGrid, setShowGrid] = useState(() => {
    const saved = localStorage.getItem('editor_showGrid');
    return saved !== null ? saved === 'true' : true;
  });
  const [snapToGuides, setSnapToGuides] = useState(() => {
    const saved = localStorage.getItem('editor_snapToGuides');
    return saved !== null ? saved === 'true' : true;
  });
  const [showCrosshair, setShowCrosshair] = useState(() => {
    const saved = localStorage.getItem('editor_showCrosshair');
    return saved !== null ? saved === 'true' : true;
  });
  const [glassType, setGlassType] = useState(() => localStorage.getItem('editor_glassType') || FULL_LARGE_GLASS_SIZE);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('editor_viewMode') || 'normal');
  const canvasRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('editor_showGrid', showGrid);
  }, [showGrid]);

  useEffect(() => {
    localStorage.setItem('editor_snapToGuides', snapToGuides);
  }, [snapToGuides]);

  useEffect(() => {
    localStorage.setItem('editor_showCrosshair', showCrosshair);
  }, [showCrosshair]);

  useEffect(() => {
    localStorage.setItem('editor_glassType', glassType);
  }, [glassType]);

  useEffect(() => {
    localStorage.setItem('editor_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('editor_theme', theme);
  }, [theme]);

  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  const syncItemsFromPaper = useCallback(() => {
    if (!paper.project) return;

    const isUserItem = (item) => {
      if (!item) return false;

      if (item instanceof paper.Layer) return false;

      if (
        item.name === 'grid' ||
        item.name === 'glassArea' ||
        item.name === 'transformOverlay' ||
        item.data?.isGuide ||
        item.data?.isSystem ||
        item.data?.isTransformHandle ||
        item.guide ||
        item.data?.isSystemOverlay
      ) return false;

      if (item.parent && (item.parent.name === 'transformOverlay' || item.parent.data?.isSystem)) return false;

      return true;
    };

    const mapItemTree = (item) => {
      if (!isUserItem(item)) return null;

      let children = [];
      if (item.className !== 'CompoundPath' && item.children && item.children.length > 0) {
        children = item.children.map(mapItemTree).filter(Boolean);
      }

      let itemName = item.name;
      if (!itemName) {
        const toolName = (item.data?.type || item.className || 'item').replace('-', '_').toLowerCase();
        itemName = `${t(`sidebar.tool_${toolName}`, { defaultValue: toolName })} #${item.id}`;
      }

      return {
        id: item.id,
        name: itemName,
        className: item.className || 'Item',
        visible: item.visible ?? true,
        locked: item.locked ?? false,
        selected: item.selected ?? false,
        children: children,
        color: item.fillColor ? item.fillColor.toCSS(true) : (item.strokeColor ? item.strokeColor.toCSS(true) : null)
      };
    };

    const allItems = [];
    paper.project.layers.forEach(layer => {
      const isSystemLayer = layer.name === 'backgroundLayer';
      
      const layerItems = layer.children || [];
      layerItems.forEach(item => {
        const mapped = mapItemTree(item);
        if (mapped) allItems.push(mapped);
      });
    });

    setItemsList(allItems);

    const selectedItems = paper.project.selectedItems || [];
    const selectedCount = selectedItems.filter(isUserItem).length;
    setSelectedCount(selectedCount);

    window.dispatchEvent(new CustomEvent('paper-project-changed'));
  }, [t]);

  const {saveToLocalStorage, clearAutoSave} = useAutoSave(
    paper.project,
    syncItemsFromPaper,
    drawGrid,
    projectName,
    setProjectName
  );

  const saveState = useCallback(() => {
    if (!paper.project) return;
    const jsonState = paper.project.exportJSON();

    undoStackRef.current.push(jsonState);
    if (undoStackRef.current.length > 30) {
      undoStackRef.current.shift();
    }
    redoStackRef.current = [];

    saveToLocalStorage?.();
  }, [saveToLocalStorage]);

  const handleSaveJSON = useCallback(() => {
    if (!paper.project) return;

    const jsonString = paper.project.exportJSON();
    const blob = new Blob([jsonString], {type: 'application/json'});
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    const sanitizedJsonName = projectName.trim().replace(/[/\\?%*:|"<>]/g, '-') || 'project';
    link.download = `${sanitizedJsonName}.json`;
    link.click();

    URL.revokeObjectURL(url);
  }, [projectName]);

  const handleLoadJSON = useCallback(
    (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonContent = e.target?.result;
          if (jsonContent && paper.project) {
            saveState();
            paper.project.clear();
            paper.project.importJSON(jsonContent);

            const mainLayer = paper.project.layers['mainLayer'];
            if (mainLayer) mainLayer.activate();

            const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
            if (nameWithoutExt) {
              setProjectName(nameWithoutExt);
            }

            syncItemsFromPaper();
            window.dispatchEvent(new CustomEvent('paper-project-changed'));
          }
        } catch (error) {
          console.error('Помилка при завантаженні JSON файлу:', error);
          alert('Не вдалося відкрити файл. Переконайся, що це коректний проєкт Paper.js.');
        }
      };
      reader.readAsText(file);
    },
    [saveState, syncItemsFromPaper]
  );

  const finalName = projectName || 'project';
  const sanitizedName = (finalName.trim()
      .replace(/[/\\?%*:|"<>]/g, '-')
      .replace(/\s+/g, '_')
    || 'laser-symbol')
    .slice(0, 10);

  const handleNewProject = useCallback(() => {
    if (window.confirm('Створити новий проект? Незбережені зміни будуть втрачені.')) {
      if (!paper.project) return;
      paper.project.clear();

      new paper.Layer({name: 'backgroundLayer'});
      const mainLayer = new paper.Layer({name: 'mainLayer'});
      mainLayer.activate();

      clearAutoSave?.();
      undoStackRef.current = [];
      redoStackRef.current = [];
      setProjectName('Новий проект');
      syncItemsFromPaper();
      window.dispatchEvent(new CustomEvent('paper-project-changed'));
    }
  }, [clearAutoSave, syncItemsFromPaper]);

  const handleExportSVG = useCallback(async (customName) => {
    if (!paper.project) return;

    const isExportable = (item) =>
      item.name !== 'grid' &&
      item.name !== 'glassArea' &&
      item.name !== 'transformOverlay' &&
      !item.data?.isGuide &&
      !item.guide;

    const selectedItems = paper.project.selectedItems.filter(isExportable);
    const itemsToExport = selectedItems.length > 0
      ? selectedItems
      : paper.project.layers.reduce((acc, layer) => {
          if (layer.name !== 'backgroundLayer') {
            const children = (layer.children || []).filter(isExportable);
            acc.push(...children);
          }
          return acc;
        }, []);

    if (itemsToExport.length === 0) {
      alert('Немає обʼєктів для експорту.');
      return;
    }

    let files;
    try {
      files = await exportSvgByColor(itemsToExport, {
        includeCosmeticStrokes: false,
         // flattenToleranceMm: FLATTEN_TOLERANCE_MM, TODO: якщо EZCAD погано їсть криві
      });
    } catch (error) {
      console.error('Помилка експорту SVG:', error);
      alert(`Не вдалося експортувати SVG: ${error.message}`);
      return;
    }

    if (!files || files.length === 0) {
      alert('Не вдалося сформувати жоден SVG-файл для експорту.');
      return;
    }

    const finalName = customName || projectName;
    const sanitizedName = (finalName.trim().replace(/[/\\?%*:|"<>]/g, '-') || 'laser-symbol')
      .slice(0, 10);

    const randomSuffix = () => String(Math.floor(10000 + Math.random() * 90000));

    files.forEach((file) => {
      const fileName = `${sanitizedName}_${file.name}_${randomSuffix()}.svg`;

      const blob = new Blob([file.svg], {type: 'image/svg+xml;charset=utf-8'});
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      URL.revokeObjectURL(url);
    });
  }, [projectName]);


  const handleDelete = useCallback(() => {
    saveState();
    const selected = paper.project.selectedItems.filter((item) => {
      if (item.name === 'grid' || item.name === 'glassArea' || item.guide || item.data?.isSystem) return false;
      return true;
    });
    
    selected.forEach((item) => {
      item.remove();
    });
    
    syncItemsFromPaper();
    window.dispatchEvent(new CustomEvent('paper-item-deleted'));
  }, [saveState, syncItemsFromPaper]);

  const handleUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const currentState = paper.project.exportJSON();
    redoStackRef.current.push(currentState);

    const previousState = undoStackRef.current.pop();
    paper.project.clear();
    paper.project.importJSON(previousState);

    const mainLayer = paper.project.layers['mainLayer'];
    if (mainLayer) mainLayer.activate();

    syncItemsFromPaper();
    window.dispatchEvent(new CustomEvent('paper-item-transform'));
    window.dispatchEvent(new CustomEvent('paper-project-changed'));
  }, [syncItemsFromPaper]);

  const handleRedo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const currentState = paper.project.exportJSON();
    undoStackRef.current.push(currentState);

    const nextState = redoStackRef.current.pop();
    paper.project.clear();
    paper.project.importJSON(nextState);

    const mainLayer = paper.project.layers['mainLayer'];
    if (mainLayer) mainLayer.activate();

    syncItemsFromPaper();
    window.dispatchEvent(new CustomEvent('paper-item-transform'));
    window.dispatchEvent(new CustomEvent('paper-project-changed'));
  }, [syncItemsFromPaper]);

  const handleGroup = useCallback(() => {
    const selected = paper.project.selectedItems.filter((item) => item.name !== 'grid');
    if (selected.length < 2) return;

    saveState();
    const group = new paper.Group(selected);
    group.name = 'Група';
    clearSelection();
    group.selected = true;

    syncItemsFromPaper();
    window.dispatchEvent(new CustomEvent('paper-item-transform'));
  }, [saveState, syncItemsFromPaper]);

  const handleUngroup = useCallback(() => {
    const selected = paper.project.selectedItems.filter((item) => item instanceof paper.Group);
    if (selected.length === 0) return;

    saveState();
    selected.forEach((group) => {
      if (group.children) {
        const children = [...group.children];
        children.forEach(child => {
          child.parent = group.parent;
          child.selected = true;
        });
        group.remove();
      }
    });

    syncItemsFromPaper();
    window.dispatchEvent(new CustomEvent('paper-item-transform'));
  }, [saveState, syncItemsFromPaper]);

  const handleBooleanOperation = useCallback(
    (type) => {
      const selected = paper.project.selectedItems.filter((item) => item.name !== 'grid');
      if (selected.length < 2) return;

      saveState();
      let resultPath = selected[0];

      for (let i = 1; i < selected.length; i++) {
        if (type === 'unite') {
          resultPath = resultPath.unite(selected[i]);
        } else if (type === 'subtract') {
          resultPath = resultPath.subtract(selected[i]);
        }
      }

      selected.forEach((item) => item.remove());
      resultPath.strokeColor = '#ef4444';
      resultPath.strokeWidth = 1.5;
      resultPath.name = type === 'unite' ? 'Об\'єднання' : 'Віднімання';
      resultPath.selected = true;

      syncItemsFromPaper();
      window.dispatchEvent(new CustomEvent('paper-item-transform'));
      window.dispatchEvent(new CustomEvent('paper-item-deleted'));
    },
    [saveState, syncItemsFromPaper]
  );

  const handleAlign = useCallback(
    (type) => {
      const selected = paper.project.selectedItems.filter((item) => item.name !== 'grid');
      if (selected.length === 0) return;

      saveState();
      const center = paper.view.center;

      selected.forEach((item) => {
        if (type === 'center') {
          item.position = center;
        }
      });
      syncItemsFromPaper();
      window.dispatchEvent(new CustomEvent('paper-item-transform'));
    },
    [saveState, syncItemsFromPaper]
  );

  const handleClearProject = useCallback(() => {
    if (!paper.project) return;

    paper.project.clear();

    new paper.Layer({name: 'backgroundLayer'});
    const mainLayer = new paper.Layer({name: 'mainLayer'});
    mainLayer.activate();

    if (clearAutoSave) {
      clearAutoSave();
    }

    setProjectName(t('new_project'));

    paper.view.update();
    syncItemsFromPaper();
    window.dispatchEvent(new CustomEvent('paper-item-deleted'));
    window.dispatchEvent(new CustomEvent('paper-project-changed'));
  }, [clearAutoSave, setProjectName, syncItemsFromPaper, t]);

  useEditorHotkeys({
    setActiveTool,
    onDelete: handleDelete,
    onUndo: handleUndo,
    onRedo: handleRedo,
    onGroup: handleGroup,
    onUngroup: handleUngroup,
    setShowGrid,
    setShowCrosshair,
    setSnapToGuides,
  });

  return (
    <div 
      data-theme={theme}
      className={`flex flex-col gap-3 p-3 w-full h-full flex-1 transition-colors duration-300 ${
        theme === 'dark' ? 'bg-zinc-950 text-white border-zinc-800' : 'bg-slate-50 text-slate-900 border-slate-200'
      } border`}
    >
      <Header
        selectedCount={selectedCount}
        projectName={projectName}
        onProjectNameChange={setProjectName}
        showLayers={showLayers}
        setShowLayers={setShowLayers}
        onExportSVG={handleExportSVG}
        onSaveJSON={handleSaveJSON}
        onLoadJSON={handleLoadJSON}
        onNewProject={handleNewProject}
        onClearProject={handleClearProject}
        theme={theme}
      />

      <div className="flex gap-3 flex-1">
        <Sidebar
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          selectedCount={selectedCount}
          onDelete={handleDelete}
          onBooleanOperation={handleBooleanOperation}
          onAlign={handleAlign}
          onGroup={handleGroup}
          onUngroup={handleUngroup}
          onProjectChange={syncItemsFromPaper}
          onProjectNameChange={setProjectName}
          theme={theme}
          onThemeChange={setTheme}
          glassType={glassType}
          setGlassType={setGlassType}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          showCrosshair={showCrosshair}
          setShowCrosshair={setShowCrosshair}
          snapToGuides={snapToGuides}
          setSnapToGuides={setSnapToGuides}
          onCenterView={() => canvasRef.current?.centerView()}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        <Canvas
          ref={canvasRef}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          onProjectChange={syncItemsFromPaper}
          customGuides={customGuides}
          setCustomGuides={setCustomGuides}
          theme={theme}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          showCrosshair={showCrosshair}
          setShowCrosshair={setShowCrosshair}
          snapToGuides={snapToGuides}
          setSnapToGuides={setSnapToGuides}
          glassType={glassType}
          setGlassType={setGlassType}
          viewMode={viewMode}
        />

        {showLayers && (
          <LayersPanel
            items={itemsList}
            onProjectChange={syncItemsFromPaper}
            theme={theme}
            showGrid={showGrid}
            setShowGrid={setShowGrid}
            showCrosshair={showCrosshair}
            setShowCrosshair={setShowCrosshair}
            snapToGuides={snapToGuides}
            setSnapToGuides={setSnapToGuides}
            onCenterView={() => canvasRef.current?.centerView()}
            viewMode={viewMode}
            setViewMode={setViewMode}
            glassType={glassType}
            setGlassType={setGlassType}
            onThemeChange={setTheme}
            onSelect={(id) => {
              const item = paper.project.getItem({id});
              if (item) {
                clearSelection();
                item.selected = true;
                syncItemsFromPaper();
              }
            }}
            onToggleVisibility={(id) => {
              const item = paper.project.getItem({id});
              if (item) {
                item.visible = !item.visible;
                syncItemsFromPaper();
              }
            }}
            onToggleLock={(id) => {
              const item = paper.project.getItem({id});
              if (item) {
                item.locked = !item.locked;
                syncItemsFromPaper();
              }
            }}
            onReorder={(id, direction) => {
              const item = paper.project.getItem({id});
              if (!item) return;
              if (direction === 'up') item.insertAbove(item.nextSibling);
              else item.insertBelow(item.previousSibling);
              syncItemsFromPaper();
            }}
            onDeleteItems={(ids) => {
              saveState();
              ids.forEach((id) => {
                const item = paper.project.getItem({id});
                if (item) item.remove();
              });
              syncItemsFromPaper();
              window.dispatchEvent(new CustomEvent('paper-item-deleted'));
            }}
          />
        )}
      </div>
    </div>
  );
}